import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { COMMON_TIMEZONES } from '../../utils/reportConfig';
import { notificationSchedulesApi } from '../../api/notificationSchedules';
import { notificationOutboxApi } from '../../api/notificationOutbox';
import { buildingCorpsApi } from '../../api/buildingCorps';

const WEEK_DAYS = [
  { value: 0, label: 'Понедельник' },
  { value: 1, label: 'Вторник' },
  { value: 2, label: 'Среда' },
  { value: 3, label: 'Четверг' },
  { value: 4, label: 'Пятница' },
  { value: 5, label: 'Суббота' },
  { value: 6, label: 'Воскресенье' },
];

const STATUS_LABELS = {
  pending: 'В очереди',
  processing: 'Отправляется',
  sent: 'Отправлено',
  failed: 'Ошибка',
};

const STATUS_COLORS = {
  pending: 'default',
  processing: 'info',
  sent: 'success',
  failed: 'error',
};

const createEmptyForm = (complexId, buildingId, timezone) => ({
  residential_complex_id: complexId || '',
  building_id: buildingId || '',
  corps_id: '',
  enabled: true,
  schedule_type: 'monthly',
  day_of_week: 0,
  day_of_month: 1,
  time_of_day: '09:00',
  timezone: timezone || 'Europe/Moscow',
  send_window_start: '',
  send_window_end: '',
  message: 'Пожалуйста, передайте показания приборов учета.',
});

const ScheduleCard = ({ item, onEdit, onDelete }) => {
  const enabledLabel = item.enabled ? 'Активен' : 'Выключен';
  const scheduleLabel =
    item.schedule_type === 'weekly'
      ? `Еженедельно • ${WEEK_DAYS.find((d) => d.value === item.day_of_week)?.label || '—'}`
      : item.schedule_type === 'daily'
        ? 'Ежедневно'
        : `Ежемесячно • ${item.day_of_month || '—'} число`;
  const nextRun = item.next_run_at ? new Date(item.next_run_at).toLocaleString() : '—';
  const lastRun = item.last_run_at ? new Date(item.last_run_at).toLocaleString() : '—';
  const windowLabel =
    item.send_window_start && item.send_window_end
      ? `${item.send_window_start}–${item.send_window_end}`
      : 'без окна';
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: item.enabled ? 'rgba(33, 150, 243, 0.04)' : 'transparent',
        borderColor: item.enabled ? 'rgba(33, 150, 243, 0.35)' : 'divider',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Stack spacing={0.75} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {item.message ? item.message.slice(0, 60) : 'Уведомление'}
            </Typography>
            <Chip size="small" label={enabledLabel} color={item.enabled ? 'success' : 'default'} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <ScheduleRoundedIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {scheduleLabel} • {item.time_of_day} • {item.timezone}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Окно отправки: {windowLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Следующий запуск: {nextRun} • Последний: {lastRun}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => onEdit(item)}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(item)}>
            <DeleteOutlineRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Paper>
  );
};

const NotificationSchedulesDialog = ({ open, onClose, buildingId, buildingMeta }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [editingId, setEditingId] = useState('');
  const [form, setForm] = useState(createEmptyForm(buildingMeta?.residential_complex_id, buildingId, buildingMeta?.report_timezone));
  const [corpsOptions, setCorpsOptions] = useState([]);
  const [loadingCorps, setLoadingCorps] = useState(false);

  const [outboxLoading, setOutboxLoading] = useState(false);
  const [outbox, setOutbox] = useState([]);
  const [outboxError, setOutboxError] = useState('');
  const [outboxStatus, setOutboxStatus] = useState('');
  const [outboxScheduleId, setOutboxScheduleId] = useState('');
  const [outboxLimit, setOutboxLimit] = useState(50);
  const [outboxOffset, setOutboxOffset] = useState(0);
  const [outboxHasNext, setOutboxHasNext] = useState(false);

  const loadSchedules = async () => {
    if (!buildingId) return;
    setLoading(true);
    setError('');
    try {
      const data = await notificationSchedulesApi.list(String(buildingId));
      setSchedules(data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось загрузить расписания');
    } finally {
      setLoading(false);
    }
  };

  const loadOutbox = async (override = {}) => {
    if (!buildingId) return;
    setOutboxLoading(true);
    setOutboxError('');
    try {
      const params = {
        building_id: String(buildingId),
        schedule_id: outboxScheduleId || undefined,
        status: outboxStatus || undefined,
        limit: outboxLimit,
        offset: outboxOffset,
        ...override,
      };
      const data = await notificationOutboxApi.list({
        building_id: params.building_id,
        schedule_id: params.schedule_id,
        status: params.status,
        limit: params.limit,
        offset: params.offset,
      });
      setOutbox(data || []);
      setOutboxHasNext((data || []).length === params.limit);
    } catch (err) {
      setOutboxError(err?.response?.data?.detail || err?.message || 'Не удалось загрузить историю');
    } finally {
      setOutboxLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setForm(createEmptyForm(buildingMeta?.residential_complex_id, buildingId, buildingMeta?.report_timezone));
      setEditingId('');
      loadSchedules();
      setOutboxOffset(0);
      loadOutbox({ offset: 0 });
    }
  }, [open, buildingId, buildingMeta?.residential_complex_id, buildingMeta?.report_timezone]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    setForm((prev) => ({
      ...prev,
      residential_complex_id: buildingMeta?.residential_complex_id || prev.residential_complex_id,
      building_id: buildingId || prev.building_id,
      timezone: prev.timezone || buildingMeta?.report_timezone || 'Europe/Moscow',
    }));
  }, [open, buildingId, buildingMeta?.residential_complex_id, buildingMeta?.report_timezone]);

  useEffect(() => {
    if (!buildingId) {
      setCorpsOptions([]);
      return;
    }
    let mounted = true;
    setLoadingCorps(true);
    buildingCorpsApi
      .getByBuilding(String(buildingId))
      .then((data) => {
        if (!mounted) return;
        setCorpsOptions(data || []);
      })
      .catch(() => {
        if (!mounted) return;
        setCorpsOptions([]);
      })
      .finally(() => {
        if (!mounted) return;
        setLoadingCorps(false);
      });
    return () => {
      mounted = false;
    };
  }, [buildingId]);

  useEffect(() => {
    if (open) {
      setOutboxOffset(0);
      loadOutbox({ offset: 0 });
    }
  }, [outboxStatus, outboxScheduleId, outboxLimit]); // eslint-disable-line

  const handleEdit = (item) => {
    setEditingId(String(item.id));
    setForm({
      residential_complex_id: item.residential_complex_id,
      building_id: item.building_id || buildingId || '',
      corps_id: item.corps_id || '',
      enabled: Boolean(item.enabled),
      schedule_type: item.schedule_type || 'monthly',
      day_of_week: item.day_of_week ?? 0,
      day_of_month: item.day_of_month ?? 1,
      time_of_day: item.time_of_day || '09:00',
      timezone: item.timezone || 'Europe/Moscow',
      send_window_start: item.send_window_start || '',
      send_window_end: item.send_window_end || '',
      message: item.message || '',
    });
  };

  const handleDelete = async (item) => {
    if (!item?.id) return;
    setSaving(true);
    try {
      await notificationSchedulesApi.remove(String(item.id));
      await loadSchedules();
      await loadOutbox();
      if (editingId === String(item.id)) {
        setEditingId('');
        setForm(createEmptyForm(buildingMeta?.residential_complex_id, buildingId, buildingMeta?.report_timezone));
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось удалить расписание');
    } finally {
      setSaving(false);
    }
  };

  const validate = () => {
    if (!buildingId) return 'Не выбран дом.';
    if (!form.residential_complex_id) return 'Нет данных ЖК для дома.';
    if (!form.time_of_day) return 'Укажите время отправки.';
    if (form.schedule_type === 'weekly' && form.day_of_week == null) {
      return 'Выберите день недели.';
    }
    if (form.schedule_type === 'monthly' && !form.day_of_month) {
      return 'Выберите день месяца.';
    }
    if (!form.message || !form.message.trim()) {
      return 'Укажите текст уведомления.';
    }
    if ((form.send_window_start && !form.send_window_end) || (!form.send_window_start && form.send_window_end)) {
      return 'Окно отправки задаётся двумя значениями.';
    }
    return '';
  };

  const normalizeTime = (value) => {
    if (!value) return null;
    const trimmed = String(value).trim();
    if (!trimmed) return null;
    return trimmed.length >= 5 ? trimmed.slice(0, 5) : trimmed;
  };

  const handleSave = async () => {
    const message = validate();
    if (message) {
      setError(message);
      return;
    }
    if (!form.residential_complex_id) {
      setError('Нет данных ЖК для дома.');
      return;
    }
    setSaving(true);
    setError('');
    const payload = {
      ...form,
      residential_complex_id: form.residential_complex_id,
      building_id: buildingId || null,
      corps_id: form.corps_id || null,
      time_of_day: normalizeTime(form.time_of_day) || '09:00',
      send_window_start: normalizeTime(form.send_window_start),
      send_window_end: normalizeTime(form.send_window_end),
      message: (form.message || '').trim(),
    };
    try {
      if (editingId) {
        await notificationSchedulesApi.update(editingId, payload);
      } else {
        await notificationSchedulesApi.create(payload);
      }
      setEditingId('');
      setForm(createEmptyForm(buildingMeta?.residential_complex_id, buildingId, buildingMeta?.report_timezone));
      await loadSchedules();
      await loadOutbox();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const detailText = Array.isArray(detail)
        ? detail.map((item) => item?.msg || item).join('; ')
        : detail;
      setError(detailText || err?.message || 'Не удалось сохранить расписание');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack spacing={0.5}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Оповещения жильцам
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Автоматическая рассылка напоминаний о передаче показаний
          </Typography>
        </Stack>
        <IconButton
          aria-label="Закрыть"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 2, md: 3 } }}>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <NotificationsActiveOutlinedIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Активные расписания
                </Typography>
              </Stack>
              {loading && <Typography color="text.secondary">Загрузка...</Typography>}
              {!loading && schedules.length === 0 && (
                <Typography color="text.secondary">
                  Пока нет настроенных расписаний. Создайте первое ниже.
                </Typography>
              )}
              {schedules.map((item) => (
                <ScheduleCard
                  key={item.id}
                  item={item}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AddCircleOutlineRoundedIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {editingId ? 'Редактирование расписания' : 'Новое расписание'}
                </Typography>
              </Stack>

              <Stack spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Тип расписания</InputLabel>
                  <Select
                    value={form.schedule_type}
                    label="Тип расписания"
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        schedule_type: event.target.value,
                      }))
                    }
                  >
                    <MenuItem value="monthly">Ежемесячно</MenuItem>
                    <MenuItem value="daily">Ежедневно</MenuItem>
                    <MenuItem value="weekly">Еженедельно</MenuItem>
                  </Select>
                </FormControl>
                {form.schedule_type === 'weekly' ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>День недели</InputLabel>
                    <Select
                      value={form.day_of_week}
                      label="День недели"
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, day_of_week: Number(event.target.value) }))
                      }
                    >
                      {WEEK_DAYS.map((day) => (
                        <MenuItem key={day.value} value={day.value}>
                          {day.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : form.schedule_type === 'monthly' ? (
                  <TextField
                    label="День месяца"
                    type="number"
                    size="small"
                    fullWidth
                    inputProps={{ min: 1, max: 31 }}
                    value={form.day_of_month}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, day_of_month: Number(event.target.value) || 1 }))
                    }
                  />
                ) : null}
                <TextField
                  label="Время отправки"
                  type="time"
                  size="small"
                  fullWidth
                  value={form.time_of_day}
                  onChange={(event) => setForm((prev) => ({ ...prev, time_of_day: event.target.value }))}
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Часовой пояс</InputLabel>
                  <Select
                    value={form.timezone || ''}
                    label="Часовой пояс"
                    onChange={(event) => setForm((prev) => ({ ...prev, timezone: event.target.value }))}
                  >
                    {COMMON_TIMEZONES.map((tz) => (
                      <MenuItem key={tz} value={tz}>
                        {tz}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    label="Окно отправки с"
                    type="time"
                    size="small"
                    fullWidth
                    value={form.send_window_start}
                    onChange={(event) => setForm((prev) => ({ ...prev, send_window_start: event.target.value }))}
                  />
                  <TextField
                    label="Окно отправки до"
                    type="time"
                    size="small"
                    fullWidth
                    value={form.send_window_end}
                    onChange={(event) => setForm((prev) => ({ ...prev, send_window_end: event.target.value }))}
                  />
                </Stack>
                <FormControl fullWidth size="small" disabled={!buildingId || loadingCorps}>
                  <InputLabel>Корпус (опционально)</InputLabel>
                  <Select
                    value={form.corps_id || ''}
                    label="Корпус (опционально)"
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, corps_id: event.target.value }))
                    }
                  >
                    <MenuItem value="">Все корпуса</MenuItem>
                    {corpsOptions.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name || c.address || 'Корпус'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.enabled}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, enabled: event.target.checked }))
                      }
                    />
                  }
                  label="Включено"
                />
                <TextField
                  label="Текст уведомления"
                  multiline
                  minRows={3}
                  fullWidth
                  value={form.message}
                  onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))}
                />
              </Stack>
              <Divider />
              <Stack direction="row" spacing={1} alignItems="center">
                <ApartmentRoundedIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  Корпус можно не указывать, тогда рассылка будет по всему дому.
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <HistoryRoundedIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  История отправок
                </Typography>
              </Stack>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
                sx={{ flexWrap: 'wrap' }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Расписание</InputLabel>
                    <Select
                      value={outboxScheduleId}
                      label="Расписание"
                      onChange={(event) => setOutboxScheduleId(event.target.value)}
                    >
                      <MenuItem value="">Все</MenuItem>
                      {schedules.map((item) => (
                        <MenuItem key={item.id} value={String(item.id)}>
                          {item.message ? item.message.slice(0, 32) : item.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Статус</InputLabel>
                    <Select
                      value={outboxStatus}
                      label="Статус"
                      onChange={(event) => setOutboxStatus(event.target.value)}
                    >
                      <MenuItem value="">Все</MenuItem>
                      <MenuItem value="pending">В очереди</MenuItem>
                      <MenuItem value="processing">Отправляется</MenuItem>
                      <MenuItem value="sent">Отправлено</MenuItem>
                      <MenuItem value="failed">Ошибка</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Строк/стр</InputLabel>
                    <Select
                      value={outboxLimit}
                      label="Строк/стр"
                      onChange={(event) => setOutboxLimit(Number(event.target.value))}
                    >
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                      <MenuItem value={200}>200</MenuItem>
                    </Select>
                  </FormControl>
                  <Button variant="outlined" onClick={() => loadOutbox({ offset: outboxOffset })} disabled={outboxLoading}>
                    Обновить
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="text"
                    disabled={outboxLoading || outboxOffset === 0}
                    onClick={() => {
                      const nextOffset = Math.max(0, outboxOffset - outboxLimit);
                      setOutboxOffset(nextOffset);
                      loadOutbox({ offset: nextOffset });
                    }}
                  >
                    Назад
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Стр. {Math.floor(outboxOffset / outboxLimit) + 1}
                  </Typography>
                  <Button
                    variant="text"
                    disabled={outboxLoading || !outboxHasNext}
                    onClick={() => {
                      const nextOffset = outboxOffset + outboxLimit;
                      setOutboxOffset(nextOffset);
                      loadOutbox({ offset: nextOffset });
                    }}
                  >
                    Вперёд
                  </Button>
                </Stack>
              </Stack>
              {outboxError && <Alert severity="error">{outboxError}</Alert>}
              {outboxLoading && <Typography color="text.secondary">Загрузка...</Typography>}
              {!outboxLoading && outbox.length === 0 && (
                <Typography color="text.secondary">Записей пока нет.</Typography>
              )}
              <Stack spacing={1}>
                {outbox.map((item) => (
                  <Paper key={item.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <Chip
                        size="small"
                        label={STATUS_LABELS[item.status] || item.status}
                        color={STATUS_COLORS[item.status] || 'default'}
                      />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {item.apartment_number ? `Кв. ${item.apartment_number}` : 'Квартира —'}
                        {item.building_name ? ` • ${item.building_name}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.sent_at ? new Date(item.sent_at).toLocaleString() : 'не отправлено'}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Telegram ID: {item.telegram_id} • Период: {item.period_key}
                    </Typography>
                    {item.last_error && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        Ошибка: {item.last_error}
                      </Typography>
                    )}
                  </Paper>
                ))}
      
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Закрыть
        </Button>
        {editingId && (
          <Button
            variant="outlined"
            onClick={() => {
              setEditingId('');
              setForm(createEmptyForm(buildingMeta?.residential_complex_id, buildingId, buildingMeta?.report_timezone));
            }}
            disabled={saving}
          >
            Сбросить
          </Button>
        )}
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationSchedulesDialog;
